import React, { useEffect, useState, useCallback } from 'react'
import { useError, useForm } from '../../hooks'
import { useFirebaseCRUD } from '../../hooks/useFirebaseCRUD'

import DropdownField from '../Form/DropdownField'
import TextField from '../Form/TextField'
import Button from '../Button'
import ContextMenu from '../ContextMenu'
import Spinner from '../Spinner'

const ONE_DAY = 3600.0 * 24.0
const DATA_RETENTION_OPTIONS = [ONE_DAY * 7, ONE_DAY * 14, ONE_DAY * 21]

export function formatRetentionLifetime(lifetime) {
  if (lifetime <= ONE_DAY) {
    return `${Math.floor(lifetime / 3600.0)} hours`
  }
  return `${Math.floor(lifetime / ONE_DAY)} days`
}

export default function ({ organization, onFinishEditing }) {
  const { getCollection, updateDocument } = useFirebaseCRUD()
  const setError = useError()

  const [userTypes, setUserTypes] = useState([])
  const [timezones, setTimezones] = useState([])
  const [loading, setLoading] = useState(true)

  const [getFieldProps, formData, formError] = useForm({
    dependencies: [userTypes, timezones]
  })

  // ✅ Fetch user types & timezones from Firestore
  useEffect(() => {
    async function fetchData() {
      setLoading(true)
      try {
        // Assuming you have collections "user-types" and "timezones" in Firestore
        const fetchedUserTypes = await getCollection('userTypes')
        // const fetchedTimezones = await getCollection('timezones')

        setUserTypes(fetchedUserTypes)
        // setTimezones(fetchedTimezones)
        const tzList = Intl.supportedValuesOf('timeZone').map((tz) => ({
          id: tz,
          name: tz
        }))
        setTimezones(tzList)
      } catch (err) {
        setError(err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault()
      const {
        name,
        defaultUserTypeId,
        dataRetentionLifetime,
        timezone,
        status,
        emergencyContactPhone
      } = formData

      try {
        await updateDocument(`organizations/${organization.id}`, {
          name,
          defaultUserTypeId,
          dataRetentionLifetime: Number(dataRetentionLifetime),
          timezone,
          status,
          emergencyContactPhone:
            emergencyContactPhone?.length !== 0 ? emergencyContactPhone : null
        })

        onFinishEditing()
      } catch (err) {
        if (err.message === 'validation_error') {
          formError(err.props)
        } else {
          setError(err)
        }
      }
    },
    [
      formData,
      organization,
      updateDocument,
      formError,
      setError,
      onFinishEditing
    ]
  )

  if (loading || !userTypes.length) {
    return <Spinner />
  }

  return (
    <form onSubmit={handleSubmit}>
      <TextField
        name='name'
        label='Name'
        defaultValue={organization.name}
        {...getFieldProps()}
      />

      <DropdownField
        name='status'
        label='Status'
        defaultValue={organization?.status}
        required={true}
        {...getFieldProps()}>
        <option value='active'>Active</option>
        <option value='inactive'>Inactive</option>
      </DropdownField>

      <DropdownField
        name='defaultUserTypeId'
        label='Default User Type'
        required={true}
        defaultValue={organization?.defaultUserType || ''}
        {...getFieldProps()}>
        {userTypes.map((userType) => (
          <option key={userType.id} value={userType.id}>
            {userType.name}
          </option>
        ))}
      </DropdownField>

      <DropdownField
        name='dataRetentionLifetime'
        label='Data Retention Lifetime'
        required={true}
        defaultValue={organization?.dataRetentionLifetime}
        {...getFieldProps()}>
        {DATA_RETENTION_OPTIONS.map((lifetime, index) => (
          <option key={index} value={lifetime}>
            {formatRetentionLifetime(lifetime)}
          </option>
        ))}
      </DropdownField>

      <DropdownField
        name='timezone'
        label='Time Zone'
        defaultValue={organization?.timezone}
        required={true}
        {...getFieldProps()}>
        {timezones.map((tz, index) => (
          <option key={index} value={tz.name}>
            {tz.name} ({tz.alternativeName})
          </option>
        ))}
      </DropdownField>

      <TextField
        name='emergencyContactPhone'
        label='Emergency Phone'
        defaultValue={organization.emergencyContactPhone}
        {...getFieldProps()}
      />

      <div className='flex justify-end'>
        <Button type='submit'>Update</Button>
      </div>
    </form>
  )
}
